import { useState } from "react";
import GuidedPhotoCapture from "./GuidedPhotoCapture";

// QuoteFlow wraps GuidedPhotoCapture with:
//   1. Customer contact form (name + email + phone) up front
//   2. POST to /api/ai-estimate when photos finish (the endpoint branches on
//      `slug` in the body to dispatch to the customer-quote path)
//   3. Result screen with quote + "Book this service" handoff
//
// Props:
//   slug          — detailer slug
//   detailerName  — display name
//   onClose       — close the modal/flow
//   onBookService — (serviceId | null) => void
//                   fired when customer accepts the quote and wants to book

const css = `
  .qf-overlay { position:fixed; inset:0; z-index:2000; background:rgba(0,0,0,.6); display:flex; justify-content:center; align-items:flex-start; overflow-y:auto; }
  .qf-close { position:fixed; top:12px; right:12px; z-index:2100; background:rgba(8,11,16,.85); color:#fff; border:1px solid var(--border); border-radius:9999px; width:36px; height:36px; cursor:pointer; font-size:18px; line-height:1; }
  .qf-close:hover { background:#1a2030; }

  .qf-screen { min-height:100vh; background:var(--bg); display:flex; justify-content:center; padding:24px; box-sizing:border-box; width:100%; }
  .qf-card { width:100%; max-width:28rem; background:var(--card); border-radius:18px; padding:32px 24px; align-self:flex-start; margin-top:32px; }

  .qf-badge { width:64px; height:64px; border-radius:16px; background:var(--blue-glow); color:var(--blue); display:flex; align-items:center; justify-content:center; margin-bottom:20px; }
  .qf-title { font-family:var(--head); font-size:22px; font-weight:800; color:var(--text); margin:0 0 6px; }
  .qf-detailer { color:var(--blue); font-size:13px; font-weight:600; margin:0 0 18px; }
  .qf-sub { color:var(--muted); font-size:14px; line-height:1.55; margin:0 0 22px; }

  .qf-field { display:flex; flex-direction:column; gap:6px; margin-bottom:14px; }
  .qf-field label { color:#cbd5e1; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:1.5px; }
  .qf-field input { background:var(--card2); border:1px solid var(--border); color:var(--text); border-radius:10px; padding:12px 14px; font-size:14px; font-family:var(--body); outline:none; }
  .qf-field input:focus { border-color:var(--blue); }

  .qf-cta { width:100%; font-weight:700; border-radius:12px; padding:14px; font-size:14px; cursor:pointer; border:none; font-family:var(--body); display:flex; align-items:center; justify-content:center; gap:8px; margin-top:8px; }
  .qf-cta-primary { background:var(--blue); color:#fff; box-shadow:0 0 20px var(--blue-glow); }
  .qf-cta-primary:hover { filter:brightness(1.05); }
  .qf-cta-primary:disabled { background:var(--card2); color:#475569; cursor:not-allowed; box-shadow:none; }
  .qf-cta-secondary { background:var(--card2); color:#cbd5e1; }
  .qf-cta-secondary:hover { background:#202738; }

  .qf-quote-range { font-family:var(--head); font-size:36px; font-weight:800; color:var(--teal); text-align:center; margin:6px 0 18px; letter-spacing:1px; }
  .qf-line-items { background:var(--card2); border:1px solid var(--border); border-radius:12px; padding:14px 16px; margin-bottom:16px; }
  .qf-line-row { display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px solid var(--border); font-size:14px; }
  .qf-line-row:last-child { border-bottom:none; }
  .qf-line-row span:first-child { color:#cbd5e1; }
  .qf-line-row span:last-child { color:var(--text); font-weight:600; }

  .qf-notes { background:rgba(59,130,246,.06); border:1px solid rgba(59,130,246,.2); border-radius:12px; padding:12px 14px; color:#cbd5e1; font-size:13px; line-height:1.55; margin-bottom:16px; }
  .qf-confidence { display:flex; align-items:center; gap:6px; color:var(--muted); font-size:12px; margin-bottom:18px; }
  .qf-confidence-dot { width:8px; height:8px; border-radius:9999px; }
  .qf-confidence-dot.high   { background:var(--teal); }
  .qf-confidence-dot.medium { background:#fbbf24; }
  .qf-confidence-dot.low    { background:#f87171; }

  .qf-followup { background:rgba(251,191,36,.08); border:1px solid rgba(251,191,36,.3); border-radius:10px; padding:10px 12px; color:#fbbf24; font-size:12px; margin-bottom:14px; }

  .qf-err { color:#f87171; background:rgba(248,113,113,.08); border:1px solid rgba(248,113,113,.25); border-radius:10px; padding:12px 14px; font-size:13px; margin-bottom:16px; }
`;

export default function QuoteFlow({ slug, detailerName, onClose, onBookService }) {
  const [phase, setPhase] = useState("contact");  // contact | photos | result | error
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [contactError, setContactError] = useState(null);
  const [result, setResult] = useState(null);
  const [errMsg, setErrMsg] = useState(null);

  const submitContact = (e) => {
    e.preventDefault();
    if (!contact.name.trim() || !contact.email.trim()) {
      setContactError("Name and email are required.");
      return;
    }
    setContactError(null);
    setPhase("photos");
  };

  const handlePhotosComplete = async ({ vehicle, photos }) => {
    try {
      const res = await fetch("/api/ai-estimate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug,
          vehicle,
          customer: contact,
          photos: photos.map((p) => ({ position: p.position, label: p.label, dataUrl: p.dataUrl })),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErrMsg(data.error || "Could not generate quote.");
        setPhase("error");
        return;
      }
      setResult({ vehicle, ...data });
      setPhase("result");
    } catch (err) {
      console.error(err);
      setErrMsg("Network error — please try again.");
      setPhase("error");
    }
  };

  return (
    <div className="qf-overlay">
      <style>{css}</style>
      <button className="qf-close" onClick={onClose} aria-label="Close">×</button>

      {phase === "contact" && (
        <div className="qf-screen">
          <form className="qf-card" onSubmit={submitContact}>
            <div className="qf-badge">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l2.5 5.5L20 10l-5.5 2.5L12 18l-2.5-5.5L4 10l5.5-2.5z" />
              </svg>
            </div>
            <h1 className="qf-title">Instant AI Quote</h1>
            <p className="qf-detailer">{detailerName}</p>
            <p className="qf-sub">Tell us how to reach you, then snap a few photos of your vehicle. You'll get a quote in under a minute.</p>

            <div className="qf-field">
              <label>Name</label>
              <input
                value={contact.name}
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
                placeholder="Jane Doe"
                required
              />
            </div>
            <div className="qf-field">
              <label>Email</label>
              <input
                type="email"
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                placeholder="you@example.com"
                required
              />
            </div>
            <div className="qf-field">
              <label>Phone (optional)</label>
              <input
                type="tel"
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                placeholder="(555) 123-4567"
              />
            </div>

            {contactError && <p className="qf-err">{contactError}</p>}

            <button type="submit" className="qf-cta qf-cta-primary">Start photos</button>
          </form>
        </div>
      )}

      {phase === "photos" && (
        <GuidedPhotoCapture onComplete={handlePhotosComplete} />
      )}

      {phase === "result" && result && (
        <div className="qf-screen">
          <div className="qf-card">
            <div className="qf-badge">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h1 className="qf-title">Your estimate</h1>
            <p className="qf-detailer">
              {[result.vehicle?.make, result.vehicle?.model].filter(Boolean).join(" ") || result.vehicle?.bodyType}
            </p>

            <div className="qf-quote-range">
              ${result.quote_low} – ${result.quote_high}
            </div>

            {Array.isArray(result.line_items) && result.line_items.length > 0 && (
              <div className="qf-line-items">
                {result.line_items.map((li, i) => (
                  <div key={i} className="qf-line-row">
                    <span>{li.label}</span>
                    <span>${li.price}</span>
                  </div>
                ))}
              </div>
            )}

            {result.condition_notes && (
              <div className="qf-notes">{result.condition_notes}</div>
            )}

            <div className="qf-confidence">
              <span className={`qf-confidence-dot ${result.confidence || "medium"}`} />
              Confidence: {result.confidence || "medium"}
            </div>

            {result.needs_followup && (
              <div className="qf-followup">
                Some photos were hard to assess — the detailer may follow up to confirm the final price.
              </div>
            )}

            <button
              className="qf-cta qf-cta-primary"
              onClick={() => onBookService?.({
                serviceId: result.suggested_service_id || null,
                bodyType:  result.vehicle?.bodyType || null,
              })}
            >
              {result.suggested_service_name
                ? `Book ${result.suggested_service_name}`
                : "Book this service"}
            </button>
            <button className="qf-cta qf-cta-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      )}

      {phase === "error" && (
        <div className="qf-screen">
          <div className="qf-card">
            <h1 className="qf-title">Couldn't build the quote</h1>
            <p className="qf-err">{errMsg}</p>
            <button className="qf-cta qf-cta-primary" onClick={() => setPhase("contact")}>
              Try again
            </button>
            <button className="qf-cta qf-cta-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
